<template>
    <main id="js-page-content" class="templates-v2" role="main" v-if="!isLoading">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('template.name') }}</div>
        </portal>

        <header class="templates-hero">
            <h1 class="templates-hero-title">{{ $t('template.name') }}</h1>
            <p class="templates-hero-sub">{{ subtitleLabel }}</p>
        </header>

        <section class="templates-card">
            <div class="templates-toolbar">
                <TemplatesFilters @load-data="loadAsyncData" :isLoading="isLoading" />
                <router-link
                    v-if="can('response-templates.create')"
                    to="/admin/template/create"
                    class="templates-btn-create"
                >
                    <i class="fal fa-plus"></i>
                    <span>{{ $t('template.new') }}</span>
                </router-link>
            </div>
            <TabTemplates @load-data="loadAsyncData" :isLoading="isLoading" />
        </section>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import TabTemplates from '@/components/Model/Admin/Template/TabTemplates.vue'
import TemplatesFilters from '@/components/Model/Admin/Template/TemplatesFilters'
import { mapActions, mapState } from 'vuex'

export default {
    name: 'Templates',
    components: {
        TabTemplates,
        TemplatesFilters
    },
    data() {
        return {
            isLoading: true
        }
    },
    created() {
        this.loadAsyncData()
    },
    computed: {
        ...mapState('admin', {
            pagination: (state) => state.pagination,
            templates: (state) => state.templates
        }),
        permissions() {
            return this.$store.state.auth.permissions
                || JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        totalCount() {
            if (this.pagination && typeof this.pagination.total === 'number') {
                return this.pagination.total
            }
            return Array.isArray(this.templates) ? this.templates.length : 0
        },
        subtitleLabel() {
            if (this.totalCount === 0) return this.$t('template.count_zero')
            const formatted = new Intl.NumberFormat(this.$i18n.locale).format(this.totalCount)
            const key = this.totalCount === 1 ? 'template.count_one' : 'template.count_other'
            return this.$t(key, { count: formatted })
        }
    },
    methods: {
        ...mapActions('admin', ['getTemplates']),
        can(permission) {
            return this.permissions.includes(permission)
        },
        loadAsyncData() {
            this.getTemplates().then(() => {
                this.isLoading = false
            })
        }
    }
}
</script>

<style scoped>
.templates-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.templates-hero {
    margin-bottom: var(--v2-space-5);
}

.templates-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.templates-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.templates-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.templates-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    padding: var(--v2-space-4) var(--v2-space-5);
    border-bottom: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
}

.templates-btn-create {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 14px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary);
    color: #fff !important;
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    text-decoration: none !important;
    transition: background var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
    white-space: nowrap;
    border: none;
    cursor: pointer;
}

.templates-btn-create:hover {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.templates-btn-create i {
    color: #fff !important;
    font-size: 0.75rem;
}

@media (max-width: 900px) {
    .templates-v2 {
        padding: 16px 18px 32px;
    }

    .templates-hero-title {
        font-size: 1.375rem;
    }

    .templates-toolbar {
        flex-direction: column;
        align-items: stretch;
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .templates-btn-create {
        justify-content: center;
    }
}
</style>
